import type { StripePlanType } from '@sine/sine-core-api-types';
import type { ISelectOption } from '@sine/sine-ui-components';

import { BillingIntervalValues } from '../../../services/billing/billing.constants';
import { type Country, type Currency, LICENSE_TYPE, type PlanOption } from '../../../services/billing/billing.types';

// The country code is reference in intlTelInput plugin for flags
export const currencies: Record<Currency, { symbol: string; country: Country }> = {
  usd: { symbol: '$', country: 'us' },
  aud: { symbol: '$', country: 'au' },
  nzd: { symbol: '$', country: 'nz' },
  gbp: { symbol: '£', country: 'gb' },
};

// I don't like this at all...
export const country2currency: Record<Country, Currency> = {
  us: 'usd',
  au: 'aud',
  nz: 'nzd',
  gb: 'gbp',
};

export const currencyList: Record<Currency, ISelectOption> = {
  usd: { label: 'USD', value: 'usd' },
  aud: { label: 'AUD', value: 'aud' },
  nzd: { label: 'NZD', value: 'nzd' },
  gbp: { label: 'GBP', value: 'gbp' },
};

export const allCurrencyOptions: Array<ISelectOption> = Object.values(currencyList);

export const THIRTY_DAYS_IN_MILLISECONDS = 2592e6;

// SysAdmin trial license only
export const FOURTEEN_DAYS_IN_MILLISECONDS = 12096e5;

export const SUBSCRIPTION_TYPE = {
  MANUAL: 'manual',
  AUTO: 'stripe',
};

export const BILLING_CYCLE = {
  YEARLY: 'year',
  MONTHLY: 'month',
  DAILY: 'day',
};

export const LEGACY_MANUAL_LICENCE_TYPE = {
  SMALL: {
    label: 'Small',
    value: 'small',
  },
  MEDIUM: {
    label: 'Medium',
    value: 'medium',
  },
  LARGE: {
    label: 'Large',
    value: 'enterprise',
  },
};

export const ENTERPRISE_MANUAL_LICENCE_TYPE = {
  ENTERPRISE: {
    label: 'Enterprise',
    value: 'unlimited',
  },
};

export const NEW_MANUAL_LICENCE_TYPE = {
  LITE: {
    label: 'Lite',
    value: 'vmslite',
  },
  PLUS: {
    label: 'Plus',
    value: 'vmsplus',
  },
  PREMIUM: {
    label: 'Premium',
    value: 'vmspremium',
  },
};

export const PLAN_HIERARCHY = ['vmslite', 'vmsplus', 'vmspremium'];

export const VMS_TIERS: Record<string, number> = {
  vmslite: 1,
  vmsplus: 2,
  vmspremium: 3,
  vmsenterprise: 4,
};

export const CMS_TIERS: Record<string, number> = {
  none: 0,
  cmslite: 1,
  cmsplus: 2,
};

export const PLAN_COMPONENTS_MAP: Record<string, { vms: string; cms: string }> = {
  vmslite: { vms: 'vmslite', cms: 'none' },
  vmsplus: { vms: 'vmsplus', cms: 'none' },
  vmspremium: { vms: 'vmspremium', cms: 'none' },
  vmsenterprise: { vms: 'vmsenterprise', cms: 'none' },

  vmslite_cmslite: { vms: 'vmslite', cms: 'cmslite' },
  vmslite_cmsplus: { vms: 'vmslite', cms: 'cmsplus' },

  vmsplus_cmslite: { vms: 'vmsplus', cms: 'cmslite' },
  vmsplus_cmsplus: { vms: 'vmsplus', cms: 'cmsplus' },

  vmspremium_cmslite: { vms: 'vmspremium', cms: 'cmslite' },
  vmspremium_cmsplus: { vms: 'vmspremium', cms: 'cmsplus' },

  vmsenterprise_cmslite: { vms: 'vmsenterprise', cms: 'cmslite' },
  vmsenterprise_cmsplus: { vms: 'vmsenterprise', cms: 'cmsplus' },
};

export const CMSPlanOptionsObject = {
  CMS_LITE: {
    name: 'Lite',
    type: 'cmslite' as const,
    description: 'Perfect for businesses needing a basic level of organized compliance',
    features: [
      'Custom forms',
      'Response submissions',
      'Inductions',
      'Incident management',
      'Maintenance requests',
      'Automated check-in/out',
      'Data retention policies',
      'Approver notifications',
    ],
    diffFromTypes: ['vmslite', 'vmslite_cmslite'],
    mostPopular: true,
  },
  CMS_PLUS: {
    name: 'Plus',
    type: 'cmsplus' as const,
    description: 'Great for organizations who want to leverage higher compliance features.',
    features: [
      'All Lite features',
      'Manageable permits',
      'Custom contractor check-in',
      'Custom multi-level forms',
      'Custom contractor flows',
      'Vendor compliance document collection',
    ],
    diffFromTypes: ['vmslite', 'vmslite_cmsplus'],
  },
};

export const CMSPlanOptions: Array<PlanOption> = Object.values(CMSPlanOptionsObject);

export const allLicenceTypeOptions: Array<ISelectOption> = [
  ...Object.values(LEGACY_MANUAL_LICENCE_TYPE),
  ...Object.values(NEW_MANUAL_LICENCE_TYPE),
  ...Object.values(ENTERPRISE_MANUAL_LICENCE_TYPE),
];

export const newLicenceTypeOptions: Array<ISelectOption> = [
  ...Object.values(NEW_MANUAL_LICENCE_TYPE),
  ...Object.values(ENTERPRISE_MANUAL_LICENCE_TYPE),
];

export enum AddonType {
  WORKFLOW = 'Workflows',
}

// We only take Workflows as selectable addon type atm
export const licenceAddonsOption = {
  WORKFLOW: { label: 'Workflows', value: AddonType.WORKFLOW },
};

export const licenceAddons: Array<ISelectOption> = [licenceAddonsOption.WORKFLOW];

// core-api-types has `SiteStatus` enum, but can't use as enum coz type dec only :(
export enum SiteStatus {
  ENABLED = 'Enabled',
  DISABLED = 'Disabled',
  ARCHIVED = 'Archived',
  TEMPLATE = 'Template',
  TRIAL = 'Trial',
}

export enum WorkflowFreeTrialStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  EXTENDED = 'extended',
  EXPIRED = 'expired',
  NOT_STARTED = 'notStarted',
  NOT_ELIGIBLE = 'notEligible',
}

export enum ProductIconBadgeText {
  TRIAL = 'TRIAL',
}

export enum WorkflowPaymentStatus {
  UNPAID = 'unpaid',
  PAID = 'payingCustomer',
  NEEDS_FOLLOW_UP = 'needsFollowUp',
}

export const WorkflowPaymentStatusOptions: Record<WorkflowPaymentStatus, ISelectOption> = {
  [WorkflowPaymentStatus.UNPAID]: {
    label: 'Unpaid',
    value: WorkflowPaymentStatus.UNPAID,
  },
  [WorkflowPaymentStatus.PAID]: {
    label: 'Paying customer',
    value: WorkflowPaymentStatus.PAID,
  },
  [WorkflowPaymentStatus.NEEDS_FOLLOW_UP]: {
    label: 'Needs follow up',
    value: WorkflowPaymentStatus.NEEDS_FOLLOW_UP,
  },
};

export const planOptions: Record<
  StripePlanType.VMSLITE | StripePlanType.VMSPLUS | StripePlanType.VMSPREMIUM,
  PlanOption
> = {
  vmslite: {
    name: 'Lite',
    description: 'Perfect for small businesses getting started with visitor management',
    features: [
      'Unlimited check-ins & invitations',
      'Custom branding (SinePoint Pro)',
      'Customizable check-in forms',
      'Fast Track',
      'Basic watchlists',
      'Geofence auto check-in',
    ],
  },
  vmsplus: {
    name: 'Plus',
    description: 'Ideal for growing businesses needing advanced visitor management',
    features: [
      'All Lite features',
      'Custom visitor type flows',
      'Pre-registration with advanced Fast Track',
      'Bulk CSV import and export',
      'Advanced Watchlists',
      'Plus even more features',
    ],
  },
  vmspremium: {
    name: 'Premium',
    description: 'Enterprise-level security and compliance for complex needs',
    features: [
      'All Lite and Plus features',
      'Facial recognition',
      'Access control',
      'SMS notifications',
      'Advanced reporting & analytics',
      'Plus even more features',
    ],
  },
};

export const billingCycleOptions = [
  { label: 'Monthly', value: BillingIntervalValues.MONTHLY, dataStExt: 'button-monthly' },
  { label: 'Annual', value: BillingIntervalValues.YEARLY, dataStExt: 'button-annual' },
];

export const VMSPlans = ['vmslite', 'vmsplus', 'vmspremium'];

export enum StripePriceBadgeName {
  VMSLITE = 'Visitors Lite',
  VMSPLUS = 'Visitors Plus',
  VMSPREMIUM = 'Visitors Premium',
  VMSLITE_CMSLITE = 'Visitors Lite includes Contractors Lite',
  VMSLITE_CMSPLUS = 'Visitors Lite includes Contractors Plus',
  VMSPLUS_CMSLITE = 'Visitors Plus includes Contractors Lite',
  VMSPLUS_CMSPLUS = 'Visitors Plus includes Contractors Plus',
  VMSPREMIUM_CMSLITE = 'Visitors Premium includes Contractors Lite',
  VMSPREMIUM_CMSPLUS = 'Visitors Premium includes Contractors Plus',
}

export const licenceTypeToBadgeNameMap: Record<string, StripePriceBadgeName> = {
  [LICENSE_TYPE.VMSLITE]: StripePriceBadgeName.VMSLITE,
  [LICENSE_TYPE.VMSLITE_CMSLITE]: StripePriceBadgeName.VMSLITE_CMSLITE,
  [LICENSE_TYPE.VMSLITE_CMSPLUS]: StripePriceBadgeName.VMSLITE_CMSPLUS,
  [LICENSE_TYPE.VMSPLUS]: StripePriceBadgeName.VMSPLUS,
  [LICENSE_TYPE.VMSPLUS_CMSLITE]: StripePriceBadgeName.VMSPLUS_CMSLITE,
  [LICENSE_TYPE.VMSPLUS_CMSPLUS]: StripePriceBadgeName.VMSPLUS_CMSPLUS,
  [LICENSE_TYPE.VMSPREMIUM]: StripePriceBadgeName.VMSPREMIUM,
  [LICENSE_TYPE.VMSPREMIUM_CMSLITE]: StripePriceBadgeName.VMSPREMIUM_CMSLITE,
  [LICENSE_TYPE.VMSPREMIUM_CMSPLUS]: StripePriceBadgeName.VMSPREMIUM_CMSPLUS,
  [LICENSE_TYPE.VMSENTERPRISE]: StripePriceBadgeName.VMSPREMIUM,
  [LICENSE_TYPE.VMSENTERPRISE_CMSLITE]: StripePriceBadgeName.VMSPREMIUM_CMSLITE,
  [LICENSE_TYPE.VMSENTERPRISE_CMSPLUS]: StripePriceBadgeName.VMSPREMIUM_CMSPLUS,
};

export const planTierRanking: Partial<Record<string, number>> = {
  [StripePriceBadgeName.VMSPREMIUM_CMSPLUS]: 9,
  [StripePriceBadgeName.VMSPREMIUM_CMSLITE]: 8,
  [StripePriceBadgeName.VMSPREMIUM]: 7,
  [StripePriceBadgeName.VMSPLUS_CMSPLUS]: 6,
  [StripePriceBadgeName.VMSPLUS_CMSLITE]: 5,
  [StripePriceBadgeName.VMSPLUS]: 4,
  [StripePriceBadgeName.VMSLITE_CMSPLUS]: 3,
  [StripePriceBadgeName.VMSLITE_CMSLITE]: 2,
  [StripePriceBadgeName.VMSLITE]: 1,
};
